import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Typography,
  Button,
  Alert,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Fab,
  Paper,
  Skeleton,
  Snackbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import {
  Add,
  GridView,
  TableRows,
  Refresh,
  PlaylistPlay,
  Edit,
  Archive,
  Emergency,
  Assignment,
  PlayCircle,
} from '@mui/icons-material';

import { useAuth, useRole } from '../../contexts/AuthContext';
import { DeviceStatus, PlaylistType, PlaylistStatus, UserRole } from "../../types";
import type { Device, Playlist, SearchFilters, SortOptions, PaginatedResponse } from "../../types";
import apiClient from '../../services/api';
import { PlaylistCard, PlaylistFilters, PlaylistDialog, PlaylistDetailsDialog, PlaylistPreviewDialog, PlaylistAssignDevicesDialog } from '../../components/playlists';
import { invalidateDeviceData, invalidatePlaylistData, invalidateScheduleData } from '../../utils/cacheInvalidation';

// Получаем список уникальных авторов из загруженных плейлистов
const getAvailableAuthors = (playlists?: Playlist[]) => {
  if (!playlists) return [];

  const uniqueAuthors = new Map();
  playlists.forEach(playlist => {
    if (playlist.creator) {
      uniqueAuthors.set(playlist.creator.id, {
        id: playlist.creator.id,
        name: playlist.creator.full_name || playlist.creator.username,
      });
    }
  });

  return Array.from(uniqueAuthors.values());
};

const PlaylistsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { canManagePlaylists } = useRole();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === UserRole.ADMIN;

  // Состояния для UI
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Состояние для диалогов
  const [playlistDialog, setPlaylistDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    playlist?: Playlist | null;
  }>({
    open: false,
    mode: 'create',
    playlist: null,
  });

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    playlist?: Playlist | null;
  }>({
    open: false,
    playlist: null,
  });

  const [detailsDialog, setDetailsDialog] = useState<{
    open: boolean;
    playlist?: Playlist | null;
  }>({
    open: false,
    playlist: null,
  });

  const [previewDialog, setPreviewDialog] = useState<{
    open: boolean;
    playlist?: Playlist | null;
  }>({
    open: false,
    playlist: null,
  });

  const [assignDevicesDialog, setAssignDevicesDialog] = useState<{
    open: boolean;
    playlist?: Playlist | null;
  }>({
    open: false,
    playlist: null,
  });

  const [forcePlayDialog, setForcePlayDialog] = useState<{
    open: boolean;
    playlist?: Playlist | null;
  }>({
    open: false,
    playlist: null,
  });
  const [selectedForceDeviceIds, setSelectedForceDeviceIds] = useState<number[]>([]);
  const [forceSyncPlayback, setForceSyncPlayback] = useState(false);
  const [forceSyncStartDelaySeconds, setForceSyncStartDelaySeconds] = useState(10);

  // Состояния для фильтрации и сортировки
  const [filters, setFilters] = useState<SearchFilters>({
    search: '',
    playlistStatuses: [],
    playlistTypes: [],
  });

  const [sortOptions, setSortOptions] = useState<SortOptions>({
    field: 'name',
    direction: 'asc',
  });

  // Используем реальные API вызовы
  const { data: playlistsResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['playlists', currentPage, pageSize, filters, sortOptions],
    queryFn: async (): Promise<PaginatedResponse<Playlist>> => {
      const apiFilters: SearchFilters = {
        search: filters.search || undefined,
        playlistStatuses: filters.playlistStatuses?.length ? filters.playlistStatuses : undefined,
        playlistTypes: filters.playlistTypes?.length ? filters.playlistTypes : undefined,
        createdBy: filters.createdBy || undefined,
        activeOnly: filters.activeOnly || undefined,
      };

      return apiClient.getPlaylists(apiFilters, sortOptions, currentPage, pageSize);
    },
    refetchInterval: 30000, // Обновляем каждые 30 секунд
    retry: 3, // Повторяем запрос при ошибке
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const { data: forceDevicesResponse, isLoading: forceDevicesLoading } = useQuery({
    queryKey: ['playlist-force-devices'],
    queryFn: () => apiClient.getDevices({
      page: 1,
      size: 100,
      is_active: true,
      sort_by: 'name',
      sort_order: 'asc',
    }),
    enabled: forcePlayDialog.open,
  });

  const forceDevices = forceDevicesResponse?.items || [];
  const onlineForceDevices = forceDevices.filter((device) => device.status === DeviceStatus.ONLINE);

  // Обработчики событий
  const handleFiltersChange = (newFilters: SearchFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSortChange = (newSort: SortOptions) => {
    setSortOptions(newSort);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      playlistStatuses: [],
      playlistTypes: [],
    });
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newViewMode: 'grid' | 'list') => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Действия с плейлистами
  const handlePlaylistPlay = async (playlist: Playlist) => {
    setForcePlayDialog({ open: true, playlist });
    setSelectedForceDeviceIds([]);
  };

  const handleForceDeviceToggle = (deviceId: number) => {
    setSelectedForceDeviceIds((current) =>
      current.includes(deviceId)
        ? current.filter((id) => id !== deviceId)
        : [...current, deviceId],
    );
  };

  const handleSelectAllOnlineForceDevices = () => {
    setSelectedForceDeviceIds(onlineForceDevices.map((device) => device.id));
  };

  const handleConfirmForcePlay = async () => {
    const playlist = forcePlayDialog.playlist;
    if (!playlist) return;

    if (selectedForceDeviceIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'Выберите хотя бы одно онлайн-устройство для запуска плейлиста',
        severity: 'error',
      });
      return;
    }

    try {
      const result = forceSyncPlayback
        ? await apiClient.createPlaybackSyncSession({
            playlist_id: playlist.id,
            device_ids: selectedForceDeviceIds,
            start_delay_seconds: forceSyncStartDelaySeconds,
          })
        : await apiClient.sendBulkDeviceCommands({
            device_ids: selectedForceDeviceIds,
            command_type: 'play_playlist',
            command_data: {
              playlist_id: playlist.id,
              name: playlist.name,
              force: true,
            },
          });

      await invalidateDeviceData(queryClient);
      setForcePlayDialog({ open: false, playlist: null });
      setSelectedForceDeviceIds([]);
      const sentCount = (result as any).devices_count ?? (result as any).commands_sent ?? (result as any).devices?.length ?? selectedForceDeviceIds.length;
      setSnackbar({
        open: true,
        message: forceSyncPlayback && (result as any).start_at
          ? `Синхронный запуск "${playlist.name}" запланирован на ${new Date((result as any).start_at).toLocaleTimeString()} для ${sentCount} устройств`
          : `Команда запуска плейлиста "${playlist.name}" отправлена на ${sentCount} устройств`,
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Ошибка запуска плейлиста: ${error}`,
        severity: 'error',
      });
    }
  };

  const handlePlaylistStop = async (playlist: Playlist) => {
    try {
      console.log('Остановка плейлиста:', playlist);
      // TODO: Добавить API вызов для остановки воспроизведения
      setSnackbar({
        open: true,
        message: t('playlists.playStopped', { name: playlist.name }),
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Ошибка остановки воспроизведения: ${error}`,
        severity: 'error',
      });
    }
  };

  const handlePlaylistEdit = (playlist: Playlist) => {
    console.log('Редактирование плейлиста:', playlist);
    setPlaylistDialog({
      open: true,
      mode: 'edit',
      playlist,
    });
  };

  const handlePlaylistDuplicate = async (playlist: Playlist) => {
    try {
      const duplicatedPlaylist = await apiClient.duplicatePlaylist(playlist.id, {
        name: `${playlist.name} (копия)`,
        copy_items: true,
      });

      setSnackbar({
        open: true,
        message: t('playlists.duplicated', { name: playlist.name }),
        severity: 'success',
      });

      await invalidatePlaylistData(queryClient);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Ошибка дублирования плейлиста: ${error}`,
        severity: 'error',
      });
    }
  };

  const handlePlaylistDelete = (playlist: Playlist) => {
    setDeleteDialog({
      open: true,
      playlist,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.playlist) return;

    try {
      await apiClient.deletePlaylist(deleteDialog.playlist.id);
      setSnackbar({
        open: true,
        message: `Плейлист "${deleteDialog.playlist.name}" удален`,
        severity: 'success',
      });
      await invalidatePlaylistData(queryClient);
      setDeleteDialog({ open: false, playlist: null });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Ошибка удаления плейлиста: ${error}`,
        severity: 'error',
      });
    }
  };

  const handlePlaylistArchive = async (playlist: Playlist) => {
    try {
      // TODO: Добавить API вызов для архивирования
      console.log('Архивирование плейлиста:', playlist);
      setSnackbar({
        open: true,
        message: t('playlists.archived', { name: playlist.name }),
        severity: 'success',
      });
      await invalidatePlaylistData(queryClient);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Ошибка архивирования плейлиста: ${error}`,
        severity: 'error',
      });
    }
  };

  const handlePlaylistUnarchive = async (playlist: Playlist) => {
    try {
      // TODO: Добавить API вызов для разархивирования
      console.log('Разархивирование плейлиста:', playlist);
      setSnackbar({
        open: true,
        message: t('playlists.unarchived', { name: playlist.name }),
        severity: 'success',
      });
      await invalidatePlaylistData(queryClient);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Ошибка разархивирования плейлиста: ${error}`,
        severity: 'error',
      });
    }
  };

  const handlePlaylistSchedule = (playlist: Playlist) => {
    console.log('Планирование плейлиста:', playlist);
    // TODO: Открыть модальное окно планирования
  };

  const handleAssignDevices = (playlist: Playlist) => {
    console.log('Назначение устройств плейлисту:', playlist);
    setAssignDevicesDialog({
      open: true,
      playlist,
    });
  };

  const handleViewDetails = (playlist: Playlist) => {
    console.log('Детали плейлиста:', playlist);
    setDetailsDialog({
      open: true,
      playlist,
    });
  };

  const handlePreview = (playlist: Playlist) => {
    console.log('Предпросмотр плейлиста:', playlist);
    setPreviewDialog({
      open: true,
      playlist,
    });
  };

  const handleCreatePlaylist = () => {
    setPlaylistDialog({
      open: true,
      mode: 'create',
      playlist: null,
    });
  };

  const handleSavePlaylist = async (playlistData: any) => {
    try {
      // Преобразуем поля формы в поля API
      const apiData = {
        name: playlistData.name,
        description: playlistData.description,
        status: playlistData.status,
        type: playlistData.type,
        loop_enabled: playlistData.is_loop,
        shuffle_enabled: playlistData.shuffle,
        is_default: !playlistData.is_public, // Инвертируем is_public обратно в is_default
      };

      if (playlistDialog.mode === 'create') {
        const newPlaylist = await apiClient.createPlaylist(apiData);
        setSnackbar({
          open: true,
          message: `Плейлист "${newPlaylist.name}" создан`,
          severity: 'success',
        });
      } else {
        const updatedPlaylist = await apiClient.updatePlaylist(
          playlistDialog.playlist!.id,
          apiData
        );
        setSnackbar({
          open: true,
          message: `Плейлист "${updatedPlaylist.name}" обновлен`,
          severity: 'success',
        });
      }

      setPlaylistDialog({ open: false, mode: 'create', playlist: null });
      await invalidatePlaylistData(queryClient);
    } catch (error) {
      const action = playlistDialog.mode === 'create' ? 'создания' : 'обновления';
      setSnackbar({
        open: true,
        message: `Ошибка ${action} плейлиста: ${error}`,
        severity: 'error',
      });
      throw error; // Re-throw для обработки в диалоге
    }
  };

  // Загрузочные скелетоны
  const renderSkeletons = () => (
    <Grid container spacing={2}>
      {Array.from({ length: pageSize }).map((_, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
          <Paper elevation={1} sx={{ p: 2, height: 350 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Skeleton variant="circular" width={48} height={48} />
              <Box sx={{ flexGrow: 1 }}>
                <Skeleton variant="text" height={24} width="80%" />
                <Skeleton variant="text" height={16} width="60%" />
              </Box>
            </Box>
            <Skeleton variant="text" height={16} width="40%" sx={{ mb: 1 }} />
            <Skeleton variant="text" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="text" height={16} width="60%" sx={{ mb: 1 }} />
            <Skeleton variant="text" height={16} width="50%" sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} />
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  // Отображение плейлистов
  const renderPlaylistsGrid = () => {
    if (!playlistsResponse?.items.length) {
      return (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('playlists.noPlaylists')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('playlists.noPlaylistsDescription')}
          </Typography>
          {canManagePlaylists() && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreatePlaylist}
            >
              {t('playlists.createFirst')}
            </Button>
          )}
        </Paper>
      );
    }

    return (
      <Grid container spacing={2}>
        {playlistsResponse.items.map((playlist) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={playlist.id}>
            <PlaylistCard
              playlist={playlist}
              currentUserRole={user?.role}
              onPlay={isAdmin ? handlePlaylistPlay : undefined}
              onStop={isAdmin ? handlePlaylistStop : undefined}
              onEdit={canManagePlaylists() ? handlePlaylistEdit : undefined}
              onDuplicate={canManagePlaylists() ? handlePlaylistDuplicate : undefined}
              onDelete={canManagePlaylists() ? handlePlaylistDelete : undefined}
              onArchive={canManagePlaylists() ? handlePlaylistArchive : undefined}
              onUnarchive={canManagePlaylists() ? handlePlaylistUnarchive : undefined}
              onSchedule={canManagePlaylists() ? handlePlaylistSchedule : undefined}
              onAssignDevices={canManagePlaylists() ? handleAssignDevices : undefined}
              onViewDetails={handleViewDetails}
              onPreview={handlePreview}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  // Статистика в заголовке
  const getStatusCount = (status: PlaylistStatus) => {
    if (!playlistsResponse?.items) return 0;
    return playlistsResponse.items.filter(p => p.status === status).length;
  };

  const getTypeCount = (type: PlaylistType) => {
    if (!playlistsResponse?.items) return 0;
    return playlistsResponse.items.filter(p => p.type === type).length;
  };

  return (
    <Box>
      {/* Заголовок страницы */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            {t('navigation.playlists')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {t('playlists.pageDescription')}
          </Typography>

          {/* Быстрая статистика */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<PlayCircle />}
              label={`${getStatusCount(PlaylistStatus.ACTIVE)} ${t('playlists.active')}`}
              color="success"
              variant="outlined"
            />
            <Chip
              icon={<Edit />}
              label={`${getStatusCount(PlaylistStatus.DRAFT)} ${t('playlists.drafts')}`}
              color="warning"
              variant="outlined"
            />
            <Chip
              icon={<Archive />}
              label={`${getStatusCount(PlaylistStatus.ARCHIVED)} ${t('playlists.archived')}`}
              color="default"
              variant="outlined"
            />
            <Chip
              icon={<Emergency />}
              label={`${getTypeCount(PlaylistType.EMERGENCY)} ${t('playlists.emergency')}`}
              color="error"
              variant="outlined"
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Переключатель вида */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="grid" aria-label="grid view">
              <GridView />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <TableRows />
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Кнопка обновления */}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            {t('common.refresh')}
          </Button>

          {/* Кнопка создания */}
          {canManagePlaylists() && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreatePlaylist}
            >
              {t('playlists.createPlaylist')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Фильтры */}
      <PlaylistFilters
        filters={filters}
        sortOptions={sortOptions}
        onFiltersChange={handleFiltersChange}
        onSortChange={handleSortChange}
        onClearFilters={handleClearFilters}
        totalItems={playlistsResponse?.total || 0}
        availableAuthors={getAvailableAuthors(playlistsResponse?.items)}
      />

      {/* Ошибка загрузки */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('common.loadError')}
        </Alert>
      )}

      {/* Контент страницы */}
      {isLoading ? renderSkeletons() : renderPlaylistsGrid()}

      {/* Пагинация */}
      {playlistsResponse && playlistsResponse.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={playlistsResponse.total_pages}
            page={currentPage}
            onChange={handlePageChange}
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* FAB для мобильных устройств */}
      {canManagePlaylists() && (
        <Fab
          color="primary"
          aria-label="create playlist"
          onClick={handleCreatePlaylist}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' },
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Уведомления */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Диалог создания/редактирования плейлиста */}
      <PlaylistDialog
        open={playlistDialog.open}
        onClose={() => setPlaylistDialog({ open: false, mode: 'create', playlist: null })}
        onSave={handleSavePlaylist}
        playlist={playlistDialog.playlist}
        mode={playlistDialog.mode}
      />

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, playlist: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('playlists.deleteConfirm')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('playlists.deleteDescription')}
          </Typography>
          {deleteDialog.playlist && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('playlists.name')}: <strong>{deleteDialog.playlist.name}</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, playlist: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {t('playlists.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог ручного запуска плейлиста */}
      <Dialog
        open={forcePlayDialog.open}
        onClose={() => setForcePlayDialog({ open: false, playlist: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Запустить плейлист на устройствах</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {forcePlayDialog.playlist?.name}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={<Switch checked={forceSyncPlayback} onChange={(event) => setForceSyncPlayback(event.target.checked)} />}
              label="Синхронно"
            />
            {forceSyncPlayback && (
              <TextField
                size="small"
                type="number"
                label="Старт через, сек"
                value={forceSyncStartDelaySeconds}
                onChange={(event) => setForceSyncStartDelaySeconds(Math.max(3, Number(event.target.value) || 10))}
                inputProps={{ min: 3, max: 300 }}
                sx={{ width: 160 }}
              />
            )}
          </Box>

          {forceDevicesLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography>{t('common.loading')}</Typography>
            </Box>
          ) : onlineForceDevices.length === 0 ? (
            <Alert severity="warning">
              Нет онлайн-устройств для запуска плейлиста.
            </Alert>
          ) : (
            <>
              <Button size="small" onClick={handleSelectAllOnlineForceDevices} sx={{ mb: 1 }}>
                Выбрать все онлайн
              </Button>
              <List dense>
                {onlineForceDevices.map((device: Device) => (
                  <ListItem
                    key={device.id}
                    disablePadding
                    secondaryAction={
                      <Chip size="small" color="success" label="online" />
                    }
                  >
                    <ListItemButton onClick={() => handleForceDeviceToggle(device.id)}>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedForceDeviceIds.includes(device.id)}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={device.name}
                        secondary={device.location || device.device_type || device.type}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForcePlayDialog({ open: false, playlist: null })}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmForcePlay}
            disabled={selectedForceDeviceIds.length === 0}
          >
            {forceSyncPlayback ? 'Запустить синхронно' : 'Запустить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог деталей плейлиста */}
      <PlaylistDetailsDialog
        open={detailsDialog.open}
        playlist={detailsDialog.playlist || null}
        onClose={() => setDetailsDialog({ open: false, playlist: null })}
        onUpdate={(updatedPlaylist) => {
          invalidatePlaylistData(queryClient);
          setDetailsDialog({ open: false, playlist: null });
        }}
        onPreview={handlePreview}
      />

      {/* Диалог предпросмотра плейлиста */}
      <PlaylistPreviewDialog
        open={previewDialog.open}
        playlist={previewDialog.playlist || null}
        onClose={() => setPreviewDialog({ open: false, playlist: null })}
      />

      {/* Диалог назначения устройств плейлисту */}
      <PlaylistAssignDevicesDialog
        open={assignDevicesDialog.open}
        playlist={assignDevicesDialog.playlist || null}
        onClose={() => setAssignDevicesDialog({ open: false, playlist: null })}
        onSuccess={() => {
          setSnackbar({
            open: true,
            message: 'Устройства успешно назначены плейлисту',
            severity: 'success',
          });
          invalidatePlaylistData(queryClient);
          invalidateScheduleData(queryClient);
        }}
      />
    </Box>
  );
};

export default PlaylistsPage;
